import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Info, Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { vertaalApiFout } from "@/lib/api";
import { useKennisOpstelhulp } from "@/lib/hooks/useKnowledge";
import type { KennisOpstelhulpVoorstel } from "@/types/api";

interface KennisOpstelhulpProps {
  /** Roept de parent aan met het AI-voorstel zodat die het formulier vult. */
  onVoorstel: (voorstel: KennisOpstelhulpVoorstel) => void;
  /** Het laatst toegepaste voorstel — voor de aandachtspunten boven het formulier. */
  voorstel: KennisOpstelhulpVoorstel | null;
}

/**
 * Inklapbaar vak "Opstellen met AI" boven het kennisitem-formulier. JP plakt
 * of dicteert ruwe info; "Stel op" laat de AI het formulier vooraf invullen.
 * De AI slaat nooit zelf op — dit is enkel een voorstel dat JP naloopt.
 */
export function KennisOpstelhulp({ onVoorstel, voorstel }: KennisOpstelhulpProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [ruweTekst, setRuweTekst] = useState("");
  const opstellen = useKennisOpstelhulp();

  function stelOp() {
    const schoon = ruweTekst.trim();
    if (schoon.length === 0) return;
    opstellen.mutate(schoon, {
      onSuccess: (res) => onVoorstel(res),
      onError: (fout) =>
        toast.error(vertaalApiFout(fout, t("admin.knowledge.ai.fout"))),
    });
  }

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-2 text-left"
          aria-expanded={open}
        >
          <div
            className="h-7 w-7 rounded-md bg-brand-cyan-soft text-brand-cyan-dark grid place-items-center"
            aria-hidden
          >
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold">
              {t("admin.knowledge.ai.titel")}
            </div>
            <div className="text-[11.5px] text-muted-foreground">
              {t("admin.knowledge.ai.uitleg")}
            </div>
          </div>
          {open ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
          )}
        </button>

        {open && (
          <>
            <div className="space-y-1.5">
              <Label
                htmlFor="ai-opstelhulp-invoer"
                className="text-[11.5px] text-muted-foreground"
              >
                {t("admin.knowledge.ai.invoer_label")}
              </Label>
              <Textarea
                id="ai-opstelhulp-invoer"
                rows={6}
                value={ruweTekst}
                onChange={(e) => setRuweTekst(e.target.value)}
                disabled={opstellen.isPending}
                className="text-[13px] leading-relaxed"
                placeholder={t("admin.knowledge.ai.invoer_placeholder")}
              />
            </div>

            <Button
              type="button"
              onClick={stelOp}
              disabled={opstellen.isPending || ruweTekst.trim().length === 0}
              className="w-full"
            >
              {opstellen.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {opstellen.isPending
                ? t("admin.knowledge.ai.bezig")
                : t("admin.knowledge.ai.stel_op")}
            </Button>

            {voorstel && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">
                  <Info className="mt-[1px] h-4 w-4 shrink-0" aria-hidden />
                  <span>{t("admin.knowledge.ai.disclaimer")}</span>
                </div>

                {voorstel.categorie_motivatie && (
                  <div className="text-[11.5px] text-muted-foreground">
                    {t("admin.knowledge.ai.categorie_motivatie", {
                      categorie: voorstel.categorie,
                      motivatie: voorstel.categorie_motivatie,
                    })}
                  </div>
                )}

                {voorstel.open_vragen.length > 0 && (
                  <div className="rounded-md border bg-muted/40 px-3 py-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {t("admin.knowledge.ai.open_vragen_titel")}
                    </div>
                    <ul className="mt-1.5 space-y-1">
                      {voorstel.open_vragen.map((vraag, i) => (
                        <li
                          key={`open-vraag-${i}`}
                          className="flex items-start gap-1.5 text-[12.5px] leading-snug"
                        >
                          <span className="mt-[6px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                          <span className="min-w-0 text-foreground">{vraag}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
